import React from "react"
import PropTypes from "prop-types"
import { FormLabel, FormGroup } from "react-bootstrap"
import { Field, propTypes } from "redux-form/immutable"
import GridToggleSelectField from "forms/components/GridToggleSelectField"
import Tooltip from "app/static/frontend/components/Tooltip"

export const handleObjectOrUri = (entity) => (typeof entity === "object" ? entity.resource_uri : entity)

/**
 * This Component renders either a TeamSelectSection or a TeamSelect
 * @name TeamSelectSection
 * @function
 * @param {Function} onChange - onChange handler that is called when the Select changes
 * @param {Boolean} shouldRenderFormGroup - gives the ability to render just the select
 * @param {Array[String]} teamUrisArray - An array of URIs to provide the select
 * @param {String} tooltipLabel - a tooltip label for the FormLabel portion of the FormGroup
 * Additional Props can be passed, such as action creators. These can be destructured in your
 * provided onChange handler
 *
 * @returns {React.Component}
 */
const TeamSelectSection = ({
    isReduxForm,
    label,
    name,
    onChange,
    shouldRenderFormGroup,
    shouldShowNoTeamsWarning,
    teamUrisArray,
    tooltipLabel,
    menuPortalTarget,
    possibleChoices = [],
    teamSelectionDisabled,
    ...props
}) => {
    // This inner helper function passes the Team objects to the provided onChange handler for non-redux forms
    // The onChange handler takes an object of arguments, where the keys are the limited_to_teams returned by the
    // select as well as the rest of the components props
    const _onChange = (teamArray) =>
        onChange({
            limited_to_teams: (teamArray || []).map(handleObjectOrUri),
            ...props,
        })

    const options = possibleChoices.map((choice) => ({
        ...choice,
        disabled: teamSelectionDisabled, // need this for GridToggle
        isDisabled: teamSelectionDisabled, // need this for Select
        isFixed: teamSelectionDisabled, // need this for select
        label: choice.name,
        value: choice.resource_uri,
    }))
    const teamSelect = (
        <div>
            {shouldShowNoTeamsWarning && teamUrisArray && !teamUrisArray.length && (
                <div className="form-section-description-warning">
                    Warning: You have selected no teams. This will be available to everyone at your Organization.
                </div>
            )}
            {!isReduxForm ? (
                <GridToggleSelectField
                    multi
                    choices={options}
                    clearable={!teamSelectionDisabled} // clearing all Teams means the Resource is unpermissioned across the organization
                    input={{
                        value: teamUrisArray,
                        onChange: _onChange,
                    }}
                    meta={{}}
                    shouldRenderNewComponents={props.shouldRenderNewComponents}
                    selectLabel={props.selectLabel}
                    defaultLabel={props.defaultLabel}
                />
            ) : (
                <Field
                    multi
                    name={name}
                    label={""}
                    menuPortalTarget={menuPortalTarget}
                    onMenuOpen={props.onMenuOpen}
                    component={GridToggleSelectField}
                    choices={options}
                    shouldRenderNewComponents={props.shouldRenderNewComponents}
                    clearable={!teamSelectionDisabled} // clearing all Teams means the Resource is unpermissioned across the organization
                />
            )}
        </div>
    )
    if (shouldRenderFormGroup) {
        return (
            <FormGroup className="form-group">
                {!props.shouldRenderNewComponents && (
                    <FormLabel className="control-label">
                        {"Teams "}
                        <Tooltip label={tooltipLabel} />
                    </FormLabel>
                )}
                {teamSelect}
            </FormGroup>
        )
    } else {
        return teamSelect
    }
}

TeamSelectSection.propTypes = {
    isReduxForm: PropTypes.bool, // Whether this component is being used in a redux-form
    onChange: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func,
    shouldRenderFormGroup: PropTypes.bool,
    shouldShowNoTeamsWarning: PropTypes.bool,
    teamUrisArray: PropTypes.array.isRequired,
    tooltipLabel: PropTypes.string,
    // this prop is true if we do NOT want the user to be able to change the teams selected, default to false
    teamSelectionDisabled: PropTypes.bool,
}

TeamSelectSection.defaultProps = {
    isReduxForm: false,
    shouldShowNoTeamsWarning: true, // In most cases, we'd like to show a warning that no teams are selected
    shouldRenderFormGroup: true, // in some instances, we'd like to render just the Team Select
    tooltipLabel: "Choose which teams should be able to access this information.",
    teamSelectionDisabled: false,
}

export default TeamSelectSection
